export const pickRandom = (arr, excludeId = null) => {
  if (!arr || arr.length === 0) return null;
  const pool = excludeId ? arr.filter((o) => o.id !== excludeId) : arr;
  if (pool.length === 0) return arr[Math.floor(Math.random() * arr.length)];
  return pool[Math.floor(Math.random() * pool.length)];
};

export const pickRandomTeam = (arr, size = 5) => {
  if (!arr || arr.length === 0) return [];
  const pool = [...arr];
  const team = [];
  const n = Math.min(size, pool.length);
  for (let i = 0; i < n; i++) {
    const idx = Math.floor(Math.random() * pool.length);
    team.push(pool[idx]);
    pool.splice(idx, 1);
  }
  return team;
};

/**
 * Pick a team of `size` that guarantees at least one operator fulfilling each
 * required role, then fills remaining slots randomly.
 * - `pool`: eligible operators (after exclusions).
 * - `requiredRoleIds`: Set of role ids to guarantee (in priority order).
 * - `roleDefs`: array of { id, operators: [operator_id, ...] }.
 *
 * Returns { team, unfilledRoles } where unfilledRoles lists role ids that
 * couldn't be satisfied (due to exclusions or size limit).
 */
export const pickRandomTeamWithRoles = (pool, size, requiredRoleIds, roleDefs) => {
  if (!pool || pool.length === 0) return { team: [], unfilledRoles: [] };
  const poolById = new Map(pool.map((o) => [o.id, o]));
  const picked = [];
  const pickedIds = new Set();
  const unfilled = [];
  const roleMap = new Map(roleDefs.map((r) => [r.id, r]));

  // Fulfill required roles (respecting size cap)
  for (const roleId of requiredRoleIds) {
    if (picked.length >= size) {
      unfilled.push(roleId);
      continue;
    }
    const role = roleMap.get(roleId);
    if (!role) continue;
    const candidates = role.operators.filter(
      (id) => poolById.has(id) && !pickedIds.has(id),
    );
    if (candidates.length === 0) {
      unfilled.push(roleId);
      continue;
    }
    const chosenId = candidates[Math.floor(Math.random() * candidates.length)];
    picked.push(poolById.get(chosenId));
    pickedIds.add(chosenId);
  }

  // Fill remaining slots with random operators
  const remaining = pool.filter((o) => !pickedIds.has(o.id));
  while (picked.length < size && remaining.length > 0) {
    const idx = Math.floor(Math.random() * remaining.length);
    picked.push(remaining[idx]);
    remaining.splice(idx, 1);
  }

  // Shuffle so required operators aren't always first
  for (let i = picked.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [picked[i], picked[j]] = [picked[j], picked[i]];
  }

  return { team: picked, unfilledRoles: unfilled };
};

export const getInitials = (name) => {
  if (!name) return "?";
  const stripped = name.replace(/[^A-Za-zÀ-ÿ]/g, "");
  return stripped.slice(0, 2).toUpperCase();
};
